import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { CampaignApi, type IResponseFullCampaign } from 'entities/Campaign'
import { ContactsApi } from 'entities/Contacts'

type ICampaignEnrolmentStoreParams = {
  data: {
    name: string
    filters: IResponseFullCampaign['filters']
    exclude_filters: IResponseFullCampaign['exclude_filters']
    id: number
    isPublished?: boolean
  }
  onNext?: () => void
}

export class CampaignEnrolmentStore {
  constructor() {
    makeAutoObservable(this)
  }
  modalId = nanoid()

  init = async (params: ICampaignEnrolmentStoreParams): Promise<void> => {
    if (!params.data.filters && !params.data.exclude_filters) return params.onNext?.()
    try {
      const { data } = await ContactsApi.getContactsFiltersCount({
        filtersListGroups: params.data.filters ?? undefined,
        excludeFiltersListGroups: params.data.exclude_filters ?? undefined,
      })
      if (data.count) {
        return new Promise((resolve) => {
          this._openModal({ ...params, enrolled_contacts_count: data.count, resolve })
        })
      } else {
        params.onNext?.()
      }
    } catch (e) {
      logger.error(e)
      params.onNext?.()
    }
  }

  private _onClose = (onNext?: () => void, resolve?: () => void) => {
    modalStore.closeModal(this.modalId)
    onNext?.()
    resolve?.()
  }

  private _openModal({
    data: { name, id, isPublished },
    onNext,
    enrolled_contacts_count,
    resolve,
  }: ICampaignEnrolmentStoreParams & { enrolled_contacts_count: number; resolve: () => void }) {
    const contactWord = enrolled_contacts_count === 1 ? 'contact' : 'contacts'
    const matchWord = enrolled_contacts_count === 1 ? 'contact matches' : 'contacts match'

    modalStore.addModal({
      id: this.modalId,
      type: ModalTypeList.SUCCESS,
      disabledOnAllClose: true,
      disabledOnEscClose: true,
      disabledOverlayClose: true,
      title: isPublished ? 'Campaign changes published' : 'Campaign activated',
      subtitle: `“${name}”`,
      desc: `${enrolled_contacts_count} ${matchWord} your campaign’s filters. Would you like to enroll them now?`,
      primaryAction: {
        text: `Yes, enroll ${enrolled_contacts_count} ${contactWord}`,
        onAction: async () => {
          try {
            await CampaignApi.enrolmentCampaign({ id })
            this._onClose(onNext, resolve)
          } catch (e) {
            logger.error(e)
            this._onClose(onNext, resolve)
          }
        },
      },
      secondaryAction: {
        text: 'No, don’t enroll',
        onAction: () => this._onClose(onNext, resolve),
      },
    })
  }
}
